import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics } from '@/mocks/comics';

type SortKey = 'newest' | 'views' | 'rating';
type StatusFilter = '全部' | '连载中' | '已完结';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'newest', label: '最新更新' },
  { key: 'views', label: '人气最高' },
  { key: 'rating', label: '评分最高' },
];

export default function ComicsPage() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || 'all';
  const keyword = params.get('q') || '';
  const [sort, setSort] = useState<SortKey>('newest');
  const [status, setStatus] = useState<StatusFilter>('全部');
  const [input, setInput] = useState(keyword);
  const [submitted, setSubmitted] = useState(keyword);

  const currentCat = categories.find((c) => c.id === category);
  const catName = currentCat?.name || '全部';

  const list = useMemo(() => {
    let arr = [...comics];
    if (category !== 'all') {
      arr = arr.filter((c) => c.tags.some((t) => currentCat?.name.includes(t) || c.tags.includes(currentCat?.name || '__')));
    }
    if (submitted) {
      arr = arr.filter(
        (c) => c.title.includes(submitted) || c.author.includes(submitted) || c.tags.some((t) => t.includes(submitted)),
      );
    }
    if (status !== '全部') {
      arr = arr.filter((c) => c.status === status);
    }
    if (sort === 'newest') {
      arr = arr.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    } else if (sort === 'views') {
      arr = arr.sort((a, b) => parseFloat(b.views) - parseFloat(a.views));
    } else {
      arr = arr.sort((a, b) => b.rating - a.rating);
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, submitted, status, sort]);

  const setCategory = (id: string) => {
    if (id === 'all') params.delete('category');
    else params.set('category', id);
    setParams(params, { replace: true });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(input.trim());
  };

  return (
    <>
      <Seo
        title={`${catName === '全部' ? '漫画大全' : `${catName}漫画`} - 麻瓜漫画网原版漫画在线看`}
        description={`麻瓜漫画网${catName === '全部' ? '漫画大全' : `${catName}栏目`}，收录高清完整版、未删减原版与导演剪辑版漫画，支持按人气、评分与最新更新排序，畅快追更不折腾。`}
        keywords={`麻瓜漫画网,${catName === '全部' ? '漫画大全,原版漫画' : `${catName}漫画,原版漫画`},高清漫画,在线看`}
        canonical="https://uncutm.github.io/comics"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `麻瓜漫画网${catName === '全部' ? '漫画大全' : catName}`,
          url: 'https://uncutm.github.io/comics',
          inLanguage: 'zh-CN',
          isPartOf: { '@type': 'WebSite', name: '麻瓜漫画网', url: 'https://uncutm.github.io/' },
        }}
      />

      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-16">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">{catName === '全部' ? '漫画大全' : catName}</span>
        </nav>

        <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
              {catName === '全部' ? '漫画大全' : catName}
            </h1>
            <p className="mt-3 text-sm text-foreground-500">
              {keyword ? `搜索关键词「${keyword}」` : '精选原版漫画'} · 共 {list.length} 部作品
            </p>
          </div>
          <form onSubmit={onSubmit} className="relative w-full max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索漫画 / 作者"
              className="h-11 w-full rounded-full bg-background-100 pl-9 pr-16 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none ring-1 ring-background-300/50 focus:ring-2 focus:ring-primary-400/60"
            />
            <button
              type="submit"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>

        {/* category filter */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="mr-1 flex items-center gap-1.5 text-sm font-semibold text-foreground-700 whitespace-nowrap">
            <i className="ri-apps-2-line text-primary-500"></i> 分类
          </span>
          <FilterChip active={category === 'all'} onClick={() => setCategory('all')}>
            全部
          </FilterChip>
          {categories.map((c) => (
            <FilterChip key={c.id} active={category === c.id} onClick={() => setCategory(c.id)}>
              {c.name}
            </FilterChip>
          ))}
        </div>

        {/* status + sort */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-background-200/60 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">状态</span>
            {(['全部', '连载中', '已完结'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`h-8 rounded-full px-4 text-xs font-semibold whitespace-nowrap transition-colors ${
                  status === s
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setSort(o.key)}
                className={`h-8 rounded-full px-4 text-xs font-semibold whitespace-nowrap transition-colors ${
                  sort === o.key ? 'bg-background-50 text-primary-500 ring-1 ring-primary-500/40' : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* list */}
        {list.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {list.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-16 flex flex-col items-center py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-search-line text-3xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-500">没有找到匹配的作品，换个关键词试试吧</p>
            <button
              type="button"
              onClick={() => {
                setInput('');
                setSubmitted('');
                setStatus('全部');
                setCategory('all');
              }}
              className="mt-4 h-10 rounded-full bg-primary-500 px-5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </>
  );
}

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-full px-4 text-sm font-medium whitespace-nowrap transition-colors ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
      }`}
    >
      {children}
    </button>
  );
}